// A single triangle that covers the viewport.
export const VERTEX_SHADER = `
attribute vec2 a_position;

void main() {
	gl_Position = vec4( a_position, 0.0, 1.0 );
}
`;

// Draws in the 1920×1080 design space of motion.js, with y pointing down. Colours are sRGB.
export const FRAGMENT_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform float u_height;   // Drawing buffer height in px.
uniform vec3 u_cover;     // Design units per buffer px, and the design-space position of the buffer's top-left.
uniform vec3 u_lobeA;     // x, y, radius.
uniform vec3 u_lobeB;     // x, y, radius.
uniform vec4 u_squircle;  // x, y, rotation, half size.
uniform float u_leftCorner; // Radius of the squircle's left corners, as a fraction of its half size.

const vec3 BG_START   = vec3( 0.325, 0.337, 0.929 );
const vec3 BG_END     = vec3( 0.376, 0.831, 0.831 );
const vec3 GLOW       = vec3( 0.737, 0.784, 0.957 );
const vec3 BLOB_START = vec3( 0.282, 0.565, 0.776 );
const vec3 BLOB_END   = vec3( 0.216, 0.345, 0.659 );
const vec3 GLASS_START = vec3( 0.620, 0.300, 0.760 );
const vec3 GLASS_END   = vec3( 0.400, 0.490, 0.770 );
const float GLASS_ALPHA = 0.5;

vec3 background( vec2 p ) {
	vec2 uv = p / vec2( 1920.0, 1080.0 );
	vec3 color = mix( BG_START, BG_END, clamp( 0.65 * uv.x + 0.35 * uv.y, 0.0, 1.0 ) );
	float glow = 1.0 - smoothstep( 0.0, 1.0, length( ( p - vec2( 420.0, 1150.0 ) ) / vec2( 1000.0, 650.0 ) ) );

	return mix( color, GLOW, 0.5 * glow );
}

// Polynomial smooth minimum; k is the width of the fillet between the two shapes.
float smin( float a, float b, float k ) {
	float h = clamp( 0.5 + 0.5 * ( b - a ) / k, 0.0, 1.0 );

	return mix( b, a, h ) - k * h * ( 1.0 - h );
}

float blobDistance( vec2 p ) {
	return smin( length( p - u_lobeA.xy ) - u_lobeA.z, length( p - u_lobeB.xy ) - u_lobeB.z, 90.0 );
}

vec3 blobColor( vec2 p ) {
	vec2 axis = u_lobeB.xy - u_lobeA.xy;
	float span = length( axis ) + u_lobeA.z + u_lobeB.z;
	float t = ( dot( p - u_lobeA.xy, normalize( axis ) ) + u_lobeA.z ) / span;

	return mix( BLOB_START, BLOB_END, clamp( t, 0.0, 1.0 ) );
}

vec2 toSquircle( vec2 p ) {
	float c = cos( u_squircle.z );
	float s = sin( u_squircle.z );
	vec2 d = p - u_squircle.xy;

	return vec2( c * d.x + s * d.y, -s * d.x + c * d.y );
}

// A rounded square whose sides bulge outwards.
float squircleDistance( vec2 q ) {
	float h = u_squircle.w;
	float radius = ( q.x < 0.0 ? u_leftCorner : 0.22 ) * h;
	vec2 a = abs( q );
	vec2 bulge = 0.1 * h * ( 1.0 - clamp( a.yx * a.yx / ( h * h ), 0.0, 1.0 ) );
	vec2 d = a - ( vec2( h - radius ) + bulge );

	return length( max( d, 0.0 ) ) + min( max( d.x, d.y ), 0.0 ) - radius;
}

float hash( vec2 p ) {
	return fract( sin( dot( p, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 );
}

void main() {
	float px = u_cover.x;
	vec2 p = u_cover.yz + vec2( gl_FragCoord.x, u_height - gl_FragCoord.y ) * px;

	vec3 color = background( p );
	float blob = 1.0 - smoothstep( -px, px, blobDistance( p ) );
	color = mix( color, blobColor( p ), blob );

	vec2 q = toSquircle( p );
	float distance = squircleDistance( q );
	float glass = 1.0 - smoothstep( -px, px, distance );

	if ( glass > 0.0 ) {
		// What lies under the glass, bent towards the rim and slightly magnified.
		vec2 normal = normalize( vec2(
			squircleDistance( q + vec2( 1.0, 0.0 ) ) - squircleDistance( q - vec2( 1.0, 0.0 ) ),
			squircleDistance( q + vec2( 0.0, 1.0 ) ) - squircleDistance( q - vec2( 0.0, 1.0 ) )
		) );
		float c = cos( u_squircle.z );
		float s = sin( u_squircle.z );
		normal = vec2( c * normal.x - s * normal.y, s * normal.x + c * normal.y );
		float rim = exp( distance / 60.0 );
		vec2 bent = u_squircle.xy + ( p - u_squircle.xy ) * 0.97 - normal * 24.0 * rim;

		float bentBlob = 1.0 - smoothstep( -px, px, blobDistance( bent ) );
		vec3 under = mix( background( p ), blobColor( bent ) * vec3( 0.93, 0.86, 0.9 ), bentBlob );

		float t = clamp( dot( p - u_squircle.xy, vec2( 0.944, 0.330 ) ) / ( 2.4 * u_squircle.w ) + 0.5, 0.0, 1.0 );
		vec3 tinted = mix( under, mix( GLASS_START, GLASS_END, t ), GLASS_ALPHA );
		tinted += 0.12 * ( 1.0 - smoothstep( 0.0, 1.5 * px, abs( distance ) ) );

		color = mix( color, tinted, glass );
	}

	// Dither to hide banding in the gradients.
	color += ( hash( gl_FragCoord.xy ) - 0.5 ) / 255.0;

	gl_FragColor = vec4( color, 1.0 );
}
`;
